/**
 * Procedimentos de END (Bloco 14) — master data registered once and then
 * selected inside each method's report.
 *
 * Deliberately NOT client-scoped: a procedure ("PR-END-001 Rev. 3") belongs
 * to the inspecting company and is applied across clients, which is also the
 * field set the specification asks for.
 */

export type NdtMethod = "ME" | "LP" | "PM" | "USC" | "BSCAN";

export const NDT_METHODS: readonly NdtMethod[] = ["ME", "LP", "PM", "USC", "BSCAN"] as const;

export const NDT_METHOD_LABELS: Record<NdtMethod, string> = {
  ME: "ME — Medição de Espessura",
  LP: "LP — Líquido Penetrante",
  PM: "PM — Partículas Magnéticas",
  USC: "US — Ultrassom Convencional",
  BSCAN: "B-SCAN"
};

/**
 * Standard "Técnica de Medição" options for ME, used both by the procedure
 * registry and by the ME report's own field. "Outro" unlocks a free-text
 * complement — no other value is invented anywhere.
 */
export const ME_MEASUREMENT_TECHNIQUES = [
  "Pulso-Eco",
  "Eco-Eco",
  "Duplo Cristal",
  "Contato Direto",
  "Imersão",
  "Outro"
] as const;

export type MeMeasurementTechnique = (typeof ME_MEASUREMENT_TECHNIQUES)[number];

export const ME_TECHNIQUE_OTHER = "Outro";

export function isOtherTechnique(technique: string | null | undefined): boolean {
  return (technique || "").trim() === ME_TECHNIQUE_OTHER;
}

/** The technique that must reach the document: the free text when "Outro" was chosen, the standard option otherwise. */
export function resolveEffectiveTechnique(technique: string | null | undefined, otherText: string | null | undefined): string {
  if (isOtherTechnique(technique)) return (otherText || "").trim();
  return (technique || "").trim();
}

export type NdtProcedureStatus = "ACTIVE" | "INACTIVE";

export interface NdtProcedureAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  /** Base64 data URL — same mechanism already used for the ME croqui in this browser runtime. */
  dataUrl: string;
  uploadedAt: string;
}

export interface NdtProcedure {
  id: string;
  method: NdtMethod;
  code: string;
  title: string;
  revision: string;
  issueDate: string;
  validityDate: string;
  referenceStandard: string;
  acceptanceCriteria: string;
  /** "Técnica Aplicável" — for ME this is one of ME_MEASUREMENT_TECHNIQUES. */
  defaultTechnique: string;
  description: string;
  status: NdtProcedureStatus;
  observations: string;
  attachments: NdtProcedureAttachment[];
  /**
   * PARÂMETROS HISTÓRICOS DO MÉTODO A (ME — medição em alta temperatura).
   *
   * Vsa continua sendo a origem técnica controlada do material e é lida deste
   * cadastro para novos cálculos. `hotCorrectionCoefficientK` e
   * `hotCorrectionReferenceTemperatureC` foram usados por uma versão anterior
   * do Método A e permanecem no payload JSON para leitura de Procedimentos,
   * snapshots e documentos históricos — o cálculo novo IGNORA esses dois
   * campos e usa, obrigatoriamente, k=1,0 m/s/°C e Tref=30 °C (constantes
   * oficiais em `me-thermal-correction.ts`). Não remover nem regravar estes
   * campos em uma migration: preservar compatibilidade histórica.
   *
   * Os três continuam opcionais no cadastro porque Procedimentos de outros
   * métodos, ou ME sem medição em alta temperatura, não precisam de Vsa.
   */
  /** Vsa — Velocidade ultrassônica de referência à temperatura ambiente (m/s), fonte dos cálculos novos. */
  hotCorrectionReferenceVelocityMs: number | null;
  /** @deprecated Compatibilidade de leitura histórica; novos cálculos usam 1,0 m/s/°C fixo. */
  hotCorrectionCoefficientK: number | null;
  /** @deprecated Compatibilidade de leitura histórica; novos cálculos usam 30 °C fixo. */
  hotCorrectionReferenceTemperatureC: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface NdtProcedureFilters {
  method?: NdtMethod | "";
  code?: string;
  title?: string;
  referenceStandard?: string;
  status?: NdtProcedureStatus | "";
}

/** Field-specific validation messages — never a generic "dados inválidos". */
export function validateNdtProcedure(input: Partial<NdtProcedure>): string[] {
  const errors: string[] = [];
  if (!input.method || !NDT_METHODS.includes(input.method as NdtMethod)) {
    errors.push("Informe o Método END (ME, LP, PM ou B-SCAN).");
  }
  if (!input.code || !input.code.trim()) errors.push("Informe o Código do Procedimento.");
  if (!input.title || !input.title.trim()) errors.push("Informe o Título / Nome do Procedimento.");
  if (input.issueDate && input.validityDate && input.validityDate < input.issueDate) {
    errors.push("A Data de Validade não pode ser anterior à Data de Emissão.");
  }
  // Os três parâmetros do Método A são opcionais no cadastro, mas quando
  // preenchidos não podem ser um valor que produziria um cálculo sem
  // sentido no relatório ME — o cadastro é a origem rastreável, e um número
  // inválido aqui contaminaria toda medição em alta temperatura que o usar.
  if (input.hotCorrectionReferenceVelocityMs !== undefined && input.hotCorrectionReferenceVelocityMs !== null) {
    if (!Number.isFinite(input.hotCorrectionReferenceVelocityMs) || input.hotCorrectionReferenceVelocityMs <= 0) {
      errors.push("O Vsa (velocidade ultrassônica de referência) deve ser um número maior que zero.");
    }
  }
  if (input.hotCorrectionCoefficientK !== undefined && input.hotCorrectionCoefficientK !== null) {
    if (!Number.isFinite(input.hotCorrectionCoefficientK)) {
      errors.push("O k (coeficiente de variação) deve ser um número válido.");
    }
  }
  if (input.hotCorrectionReferenceTemperatureC !== undefined && input.hotCorrectionReferenceTemperatureC !== null) {
    if (!Number.isFinite(input.hotCorrectionReferenceTemperatureC)) {
      errors.push("A Temperatura de Referência deve ser um número válido.");
    }
  }
  return errors;
}

/** "PR-001 — Medição de Espessura por Ultrassom (Rev. 02)" */
export function formatProcedureLabel(procedure: NdtProcedure): string {
  const revision = procedure.revision ? ` (Rev. ${procedure.revision})` : "";
  return `${procedure.code} — ${procedure.title}${revision}`;
}

/**
 * Which procedures a report may offer: only ACTIVE ones, plus the one the
 * report is already linked to (so reopening an older report never loses or
 * silently rewrites its procedure, even after it was inactivated).
 */
export function selectableProceduresForReport(
  procedures: readonly NdtProcedure[],
  method: NdtMethod,
  currentlyLinkedProcedureId?: string | null
): NdtProcedure[] {
  return procedures.filter(p =>
    p.method === method &&
    (p.status === "ACTIVE" || (!!currentlyLinkedProcedureId && p.id === currentlyLinkedProcedureId))
  );
}

export function applyProcedureFilters(
  procedures: readonly NdtProcedure[],
  filters: NdtProcedureFilters
): NdtProcedure[] {
  const contains = (value: string, term?: string) =>
    !term || !term.trim() || value.toLowerCase().includes(term.trim().toLowerCase());

  return procedures.filter(p => {
    if (filters.method && p.method !== filters.method) return false;
    if (filters.status && p.status !== filters.status) return false;
    if (!contains(p.code, filters.code)) return false;
    if (!contains(p.title, filters.title)) return false;
    if (!contains(p.referenceStandard, filters.referenceStandard)) return false;
    return true;
  });
}
